import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import CurrencyInput from '../components/CurrencyInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatCurrency, formatDate, getStatusLabel, getTodayDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Users,
  Phone,
  Mail,
  Search,
  UserPlus,
  AlertCircle,
  Link,
  Upload,
  Image,
  Eye,
  X,
  Wallet,
  DoorOpen,
  Check
} from 'lucide-react';
import Pagination from '../components/Pagination';
import { MobileCardContainer, MobileCard, MobileCardHeader, MobileCardTitle, MobileCardContent, MobileCardRow, MobileCardFooter } from '../components/MobileCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 10;

const TenantForm = ({ tenant, rooms, unlinkedUsers, selectedUser, selectedRequest, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    user_id: selectedUser?.id || tenant?.user_id || '',
    name: selectedUser?.name || tenant?.name || '',
    email: selectedUser?.email || tenant?.email || '',
    phone: selectedUser?.phone || tenant?.phone || '',
    id_number: tenant?.id_number || '',
    ktp_photo: tenant?.ktp_photo || '',
    room_id: selectedRequest?.room_id || tenant?.room_id || '',
    start_date: tenant?.start_date || getTodayDate(),
    end_date: tenant?.end_date || '',
    deposit: tenant?.deposit || 0
  });
  const [loading, setLoading] = useState(false);
  const [useExistingUser, setUseExistingUser] = useState(!!selectedUser);
  const [uploadingKtp, setUploadingKtp] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleKtpUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format file tidak didukung. Gunakan JPG, PNG, atau WebP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingKtp(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/ktp`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, ktp_photo: response.data.url }));
      toast.success('Foto KTP berhasil diupload');
    } catch (error) {
      console.error('Error uploading KTP:', error);
      toast.error('Gagal mengupload foto KTP');
    } finally {
      setUploadingKtp(false);
    }
  };

  const removeKtpPhoto = () => {
    setFormData(prev => ({ ...prev, ktp_photo: '' }));
  };

  const handleSelectUser = (userId) => {
    if (userId === 'manual') {
      setUseExistingUser(false);
      setFormData(prev => ({ ...prev, user_id: '', name: '', email: '', phone: '' }));
    } else {
      const user = unlinkedUsers.find(u => u.id === userId);
      if (user) {
        setUseExistingUser(true);
        setFormData(prev => ({
          ...prev,
          user_id: user.id,
          name: user.name,
          email: user.email,
          phone: user.phone
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.id_number || !formData.room_id) {
      toast.error('Mohon isi semua field yang wajib');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        deposit: parseFloat(formData.deposit) || 0,
        user_id: formData.user_id || null
      };

      if (tenant?.id) {
        await axios.put(`${API_URL}/api/tenants/${tenant.id}`, payload);
        toast.success('Data penghuni berhasil diperbarui');
      } else {
        // If this is from a room request, approve it first
        if (selectedRequest?.id) {
          await axios.post(`${API_URL}/api/room-requests/${selectedRequest.id}/approve`, null, {
            params: { room_id: formData.room_id }
          });
        }
        await axios.post(`${API_URL}/api/tenants`, payload);
        toast.success('Penghuni berhasil ditambahkan');
      }
      onSave();
    } catch (error) {
      console.error('Error saving tenant:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data penghuni');
    } finally {
      setLoading(false);
    }
  };

  const availableRooms = rooms.filter(r => r.status === 'kosong' || r.id === tenant?.room_id);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Link to existing user option - only for new tenants */}
      {!tenant?.id && unlinkedUsers.length > 0 && (
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 space-y-3">
          <div className="flex items-center gap-2 text-blue-700">
            <UserPlus className="w-4 h-4" />
            <span className="text-sm font-medium">Hubungkan dengan akun terdaftar</span>
          </div>
          <Select 
            value={formData.user_id || "manual"} 
            onValueChange={handleSelectUser}
          >
            <SelectTrigger className="h-10 bg-white border-blue-200 rounded-xl" data-testid="select-user">
              <SelectValue placeholder="Pilih user atau input manual" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Input Manual (tanpa akun)</SelectItem>
              {unlinkedUsers.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name} - {user.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-blue-600">
            User yang dipilih akan bisa login dan melihat data kamar & pembayarannya
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="name">Nama Lengkap *</Label>
        <Input
          id="name"
          name="name"
          placeholder="Nama lengkap penghuni"
          value={formData.name}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          disabled={useExistingUser}
          data-testid="tenant-name-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="email@contoh.com"
            value={formData.email}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            disabled={useExistingUser}
            data-testid="tenant-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">No. Telepon *</Label>
          <Input
            id="phone"
            name="phone"
            placeholder="08xxxxxxxxxx"
            value={formData.phone}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            disabled={useExistingUser}
            data-testid="tenant-phone-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="id_number">No. KTP *</Label>
        <Input
          id="id_number"
          name="id_number"
          placeholder="Nomor KTP"
          value={formData.id_number}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="tenant-ktp-input"
        />
      </div>

      {/* KTP Photo Upload */}
      <div className="space-y-2">
        <Label>Foto KTP (opsional)</Label>
        {formData.ktp_photo ? (
          <div className="relative w-full h-40 rounded-xl overflow-hidden border-2 border-dashed border-primary/30 bg-muted">
            <img 
              src={formData.ktp_photo.startsWith('http') ? formData.ktp_photo : `${API_URL}${formData.ktp_photo}`} 
              alt="Foto KTP" 
              className="w-full h-full object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => window.open(formData.ktp_photo.startsWith('http') ? formData.ktp_photo : `${API_URL}${formData.ktp_photo}`, '_blank')}
              >
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={removeKtpPhoto}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/30 rounded-xl cursor-pointer bg-muted hover:bg-muted/80 transition-colors">
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              onChange={handleKtpUpload}
              data-testid="ktp-upload-input"
            />
            {uploadingKtp ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 spinner" />
                <span className="text-sm text-muted-foreground">Mengupload...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="p-3 rounded-full bg-primary/10">
                  <Upload className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">Klik untuk upload foto KTP</span>
                <span className="text-xs text-muted-foreground">JPG, PNG, WebP (max 5MB)</span>
              </div>
            )}
          </label>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="room_id">Kamar *</Label>
        <Select value={formData.room_id} onValueChange={(value) => setFormData(prev => ({ ...prev, room_id: value }))}>
          <SelectTrigger className="h-12 bg-muted border-0 rounded-xl" data-testid="tenant-room-select">
            <SelectValue placeholder="Pilih kamar" />
          </SelectTrigger>
          <SelectContent>
            {availableRooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.name} - {formatCurrency(room.price)}/bln
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {availableRooms.length === 0 && (
          <p className="text-sm text-destructive">Tidak ada kamar kosong tersedia</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start_date">Tanggal Mulai *</Label>
          <Input
            id="start_date"
            name="start_date"
            type="date"
            value={formData.start_date}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="tenant-start-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="end_date">Tanggal Selesai</Label>
          <Input
            id="end_date"
            name="end_date"
            type="date"
            value={formData.end_date}
            onChange={handleChange}
            className="h-12 bg-muted border-0 rounded-xl"
            data-testid="tenant-end-date-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="deposit">Deposit</Label>
        <CurrencyInput
          id="deposit"
          name="deposit"
          placeholder="0"
          value={formData.deposit}
          onChange={handleChange}
          className="h-12 bg-muted border-0 rounded-xl"
          data-testid="tenant-deposit-input"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
          Batal
        </Button>
        <Button type="submit" disabled={loading} className="rounded-xl" data-testid="tenant-submit-btn">
          {loading ? <div className="w-5 h-5 spinner" /> : tenant?.id ? 'Simpan' : 'Tambah'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const Penghuni = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [unlinkedUsers, setUnlinkedUsers] = useState([]);
  const [roomRequests, setRoomRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, aktif: 0, totalDeposit: 0 });

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('skip', (page * ITEMS_PER_PAGE).toString());
      if (searchQuery) params.append('search', searchQuery);

      const [tenantsRes, roomsRes, unlinkedRes, allTenantsRes, roomRequestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tenants?${params.toString()}`),
        axios.get(`${API_URL}/api/rooms`),
        axios.get(`${API_URL}/api/users/penghuni-unlinked`),
        axios.get(`${API_URL}/api/tenants?limit=1000`),
        axios.get(`${API_URL}/api/room-requests`)
      ]);
      setTenants(tenantsRes.data.data || []);
      setTotalItems(tenantsRes.data.total || 0);
      setRooms(roomsRes.data);
      setUnlinkedUsers(unlinkedRes.data);
      setRoomRequests(roomRequestsRes.data || []);
      
      // Calculate stats from all tenants
      const allTenants = allTenantsRes.data.data || [];
      setStats({
        total: allTenantsRes.data.total || 0,
        aktif: allTenants.filter(t => t.status === 'aktif').length,
        totalDeposit: allTenants.reduce((sum, t) => sum + (t.deposit || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery]);

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
  };

  const handleDelete = (tenant) => {
    setSelectedTenant(tenant);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/tenants/${selectedTenant.id}`);
      toast.success('Penghuni berhasil dihapus');
      fetchData();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus penghuni');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
    }
  };

  const handleRejectUser = (user) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  const confirmRejectUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.delete(`${API_URL}/api/users/${selectedUser.id}/reject`);
      toast.success(`Pendaftaran ${selectedUser.name} berhasil ditolak`);
      fetchData();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error(error.response?.data?.detail || 'Gagal menolak pendaftaran');
    } finally {
      setRejectDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleApproveRequest = async (request) => {
    setSelectedRequest(request);
    // Pre-fill user data and open dialog
    setSelectedUser({
      id: request.user_id,
      name: request.user_name,
      email: request.user_email,
      phone: request.user_phone
    });
    setDialogOpen(true);
  };

  const handleRejectRequest = async (request) => {
    try {
      await axios.post(`${API_URL}/api/room-requests/${request.id}/reject`);
      toast.success('Request kamar ditolak');
      fetchData();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Gagal menolak request');
    }
  };

  const handleSave = () => {
    setDialogOpen(false);
    setSelectedTenant(null);
    setSelectedUser(null);
    setSelectedRequest(null);
    fetchData();
  };

  // Reset page when search changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="penghuni-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Penghuni</h1>
          <p className="text-muted-foreground mt-1">Kelola data penghuni kost</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-tenant-btn">
          <Plus className="w-5 h-5 mr-2" />
          Tambah Penghuni
        </Button>
      </div>

      {/* Alert for unlinked users */}
      {unlinkedUsers.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="p-4 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="font-medium text-amber-800">
                {unlinkedUsers.length} user mendaftar sebagai penghuni
              </p>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              Setujui untuk menghubungkan ke kamar atau tolak pendaftaran.
            </p>
          </div>
          <div className="divide-y divide-amber-200">
            {unlinkedUsers.map(user => (
              <div key={user.id} className="flex items-center justify-between p-4 hover:bg-amber-100/50">
                <div className="flex-1">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email} • {user.phone}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleRejectUser(user)}
                    data-testid={`reject-user-${user.id}`}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Tolak
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setDialogOpen(true);
                    }}
                    data-testid={`approve-user-${user.id}`}
                  >
                    <Link className="w-4 h-4 mr-1" />
                    Setujui
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Room Requests */}
      {roomRequests.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <div className="p-4 border-b border-blue-200">
            <div className="flex items-center gap-2">
              <DoorOpen className="w-5 h-5 text-blue-600" />
              <p className="font-medium text-blue-800">
                {roomRequests.length} request kamar baru
              </p>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Penghuni meminta untuk masuk ke kamar tertentu.
            </p>
          </div>
          <div className="divide-y divide-blue-200">
            {roomRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between p-4 hover:bg-blue-100/50">
                <div className="flex-1">
                  <p className="font-medium">{req.user_name}</p>
                  <p className="text-sm text-muted-foreground">{req.user_email} • {req.user_phone}</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Request: <span className="font-medium">{req.room_name}</span> ({formatCurrency(req.room_price)}/bln)
                  </p>
                  {req.message && (
                    <p className="text-xs text-muted-foreground mt-1 italic">&ldquo;{req.message}&rdquo;</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleRejectRequest(req)}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Tolak
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => handleApproveRequest(req)}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Setujui
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-2xl font-heading font-bold">{stats.total}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Penghuni</p>
        </Card>
        <Card className="p-4">
          <p className="text-2xl font-heading font-bold text-emerald-600">
            {stats.aktif}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Aktif</p>
        </Card>
        <Card className="p-4 col-span-2 sm:col-span-1">
          <p className="text-xl sm:text-2xl font-heading font-bold text-primary truncate">
            {formatCurrency(stats.totalDeposit)}
          </p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Deposit</p>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="Cari penghuni atau kamar..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-12 pl-12 bg-muted border-0 rounded-xl"
          data-testid="search-tenant-input"
        />
      </div>

      {/* Tenant Table */}
      {loading ? (
        <Card className="p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </Card>
      ) : tenants.length === 0 ? (
        <Card className="p-8">
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p className="empty-state-title">
              {searchQuery ? 'Penghuni tidak ditemukan' : 'Belum ada penghuni'}
            </p>
            <p className="empty-state-description">
              {searchQuery ? 'Coba kata kunci lain' : 'Tambahkan penghuni pertama Anda'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setDialogOpen(true)} className="mt-4 rounded-xl">
                <Plus className="w-5 h-5 mr-2" />
                Tambah Penghuni
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <MobileCardContainer>
            {tenants.map((tenant) => (
              <MobileCard key={tenant.id} testId={`tenant-card-${tenant.id}`}>
                <MobileCardHeader>
                  <MobileCardTitle>
                    <button 
                      onClick={() => navigate(`/penghuni/${tenant.id}`)}
                      className="font-semibold hover:text-primary hover:underline text-left"
                    >
                      {tenant.name}
                    </button>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tenant.user_id && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          <Link className="w-3 h-3 mr-1" />Linked
                        </Badge>
                      )}
                      {tenant.ktp_photo && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <Image className="w-3 h-3 mr-1" />KTP
                        </Badge>
                      )}
                    </div>
                  </MobileCardTitle>
                  <Badge className={`status-${tenant.status}`}>
                    {getStatusLabel(tenant.status)}
                  </Badge>
                </MobileCardHeader>
                <MobileCardContent>
                  <MobileCardRow label="Kamar" value={<Badge variant="outline">{tenant.room_name}</Badge>} />
                  <MobileCardRow label="KTP" value={tenant.id_number} />
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-3 h-3 text-muted-foreground" />
                    <span>{tenant.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-3 h-3 text-muted-foreground" />
                    <span className="truncate">{tenant.email}</span>
                  </div>
                  <MobileCardRow 
                    label="Deposit" 
                    value={formatCurrency(tenant.deposit || 0)} 
                    valueClassName={tenant.deposit > 0 ? 'text-green-600' : ''} 
                  />
                  <MobileCardRow 
                    label="Mulai" 
                    value={formatDate(tenant.start_date)} 
                  />
                </MobileCardContent>
                <MobileCardFooter>
                  <Button variant="outline" size="sm" onClick={() => navigate(`/penghuni/${tenant.id}`)}>
                    <Eye className="w-3 h-3 mr-1" />Detail
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/penghuni/${tenant.id}`)}>
                        <Eye className="w-4 h-4 mr-2" />Lihat Detail
                      </DropdownMenuItem>
                      {tenant.ktp_photo && (
                        <DropdownMenuItem onClick={() => window.open(tenant.ktp_photo.startsWith('http') ? tenant.ktp_photo : `${API_URL}${tenant.ktp_photo}`, '_blank')}>
                          <Image className="w-4 h-4 mr-2" />Lihat KTP
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleEdit(tenant)}>
                        <Pencil className="w-4 h-4 mr-2" />Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(tenant)} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" />Hapus
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </MobileCardFooter>
              </MobileCard>
            ))}
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </MobileCardContainer>

          {/* Desktop Table View */}
          <Card className="overflow-hidden hidden md:block">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Penghuni</TableHead>
                  <TableHead>Kamar</TableHead>
                  <TableHead>Kontak</TableHead>
                  <TableHead>Deposit</TableHead>
                  <TableHead>Periode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => navigate(`/penghuni/${tenant.id}`)}
                            className="font-medium hover:text-primary hover:underline text-left"
                            data-testid={`tenant-name-${tenant.id}`}
                          >
                            {tenant.name}
                          </button>
                          {tenant.user_id && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              <Link className="w-3 h-3 mr-1" />Linked
                            </Badge>
                          )}
                          {tenant.ktp_photo && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              <Image className="w-3 h-3 mr-1" />KTP
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">KTP: {tenant.id_number}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{tenant.room_name}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3 h-3 text-muted-foreground" />
                          {tenant.phone}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          {tenant.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center gap-2 text-sm ${tenant.deposit > 0 ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                        <Wallet className="w-3 h-3" />
                        {formatCurrency(tenant.deposit || 0)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{formatDate(tenant.start_date)}</p>
                        {tenant.end_date && (
                          <p className="text-muted-foreground">s/d {formatDate(tenant.end_date)}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`status-${tenant.status}`}>
                        {getStatusLabel(tenant.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`tenant-menu-${tenant.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/penghuni/${tenant.id}`)} data-testid={`view-detail-${tenant.id}`}>
                            <Eye className="w-4 h-4 mr-2" />Lihat Detail
                          </DropdownMenuItem>
                          {tenant.ktp_photo && (
                            <DropdownMenuItem onClick={() => window.open(tenant.ktp_photo.startsWith('http') ? tenant.ktp_photo : `${API_URL}${tenant.ktp_photo}`, '_blank')} data-testid={`view-ktp-${tenant.id}`}>
                              <Image className="w-4 h-4 mr-2" />Lihat Foto KTP
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleEdit(tenant)} data-testid={`edit-tenant-${tenant.id}`}>
                            <Pencil className="w-4 h-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(tenant)} className="text-destructive" data-testid={`delete-tenant-${tenant.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" />Hapus
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={setPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </Card>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedTenant ? 'Edit Penghuni' : 'Tambah Penghuni Baru'}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant ? 'Perbarui data penghuni' : 'Isi data penghuni yang akan ditambahkan'}
            </DialogDescription>
          </DialogHeader>
          <TenantForm
            tenant={selectedTenant}
            rooms={rooms}
            unlinkedUsers={unlinkedUsers}
            selectedUser={selectedUser}
            selectedRequest={selectedRequest}
            onSave={handleSave}
            onClose={() => {
              setDialogOpen(false);
              setSelectedTenant(null);
              setSelectedUser(null);
              setSelectedRequest(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Penghuni?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menghapus penghuni &quot;{selectedTenant?.name}&quot;? Kamar yang ditempati akan menjadi kosong.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-delete-tenant"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject User Confirmation */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tolak Pendaftaran?</AlertDialogTitle>
            <AlertDialogDescription>
              Anda yakin ingin menolak pendaftaran &quot;{selectedUser?.name}&quot; ({selectedUser?.email})? User ini akan dihapus dari sistem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRejectUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
              data-testid="confirm-reject-user"
            >
              Tolak
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Penghuni;
